<template>
  <DocumentCard class="component-wrapper" :data="reference" :index="index" @title-clicked="openReferenceDialog">
    <ExpansionPanel
      v-if="reference.pageNumber"
      v-model="isExpanded"
      class="referenced-panel"
      :title="referencedPanelTitle"
    >
      <div class="referenced-pages">
        <div class="image-container" v-for="page in reference.pageNumber" :key="page">
          <img class="reference-thumb" :src="getThumbSource(page)" alt="thumb" @click="previewDocument(page)" />
          <span class="page-index">{{ t('common.page') }} {{ page }}</span>
        </div>
      </div>
    </ExpansionPanel>
  </DocumentCard>
  <WarningPopup
    :popup-opened="missingCollectionPopupOpened"
    title-key="errors.somethingWentWrong"
    warning-message-key="collections.collectionNotFound"
    @update:open="missingCollectionPopupOpened = false"
    @execute:action="missingCollectionPopupOpened = false"
  />
</template>

<script>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { CURRENT_DIALOG } from '~/constants/conversationModels';

import useDocuments from '~/composables/useDocuments';
import useModals from '~/composables/useModals';
import ExpansionPanel from '~/components/common/ExpansionPanel';
import useCollections from '~/composables/useCollections';
import WarningPopup from '~/components/common/WarningPopup';
import DocumentCard from '~/components/conversation/chatMessage/DocumentCard';
import validateUrl from '~/utils/url';

export default {
  name: 'ReferenceCard',
  components: { DocumentCard, ExpansionPanel, WarningPopup },
  props: {
    index: {
      type: Number,
      default: 0,
    },
    documentType: {
      type: String,
      default: 'PDF',
    },
    reference: {
      type: Object,
      default: () => {},
    },
    collectionId: {
      type: String,
      default: '',
    },
    collectionName: {
      type: String,
      default: '',
    },
  },
  emits: ['refreshData'],
  setup(props, { emit }) {
    const { showPreviewDocument, setCurrentDocumentsDialog } = useModals();
    const { setSelectedDocument } = useDocuments();
    const { openModalInCollectionScope, currentCollections } = useCollections();
    const { t } = useI18n();
    const isExpanded = ref(false);

    const missingCollectionPopupOpened = ref(false);

    const referencedPageUrl = computed(() => props.reference.pages?.[props.reference.pageNumber]?.small || '');
    const firstPageUrl = computed(() => props.reference.pages?.[1]?.small || '');
    const getThumbSource = (pageNumber) =>
      validateUrl(props.reference.pages?.[pageNumber]?.small) ? props.reference.pages?.[pageNumber]?.small : '';

    const referencedPanelTitle = computed(
      () => props.reference.pageNumber.length + t('conversation.referenceCard.pagesReferenced'),
    );

    const isAvailableCollection = computed(() => {
      return currentCollections.value.findIndex((collection) => collection.collectionId === props.collectionId) > -1;
    });

    const openReferenceDialog = async () => {
      // open document in collection scope
      if (props.collectionId && props.collectionId !== 'vault') {
        if (!isAvailableCollection.value) {
          missingCollectionPopupOpened.value = true;
          return;
        }
        await openModalInCollectionScope(props.collectionId, props.collectionName, props.reference);
      } else {
        setCurrentDocumentsDialog(CURRENT_DIALOG.document_panel);
        await setSelectedDocument(props.reference);
      }
    };

    const previewDocument = async (page) => {
      await setSelectedDocument(props.reference);
      showPreviewDocument(page);
    };

    watch(isExpanded, (val) => {
      if (val) {
        emit('refreshData');
      }
    });

    return {
      firstPageUrl,
      getThumbSource,
      missingCollectionPopupOpened,
      previewDocument,
      referencedPageUrl,
      referencedPanelTitle,
      openReferenceDialog,
      isExpanded,
      t,
    };
  },
};
</script>

<style lang="scss" scoped>
@import '../../../assets/styles/variables';

.component-wrapper {
  .referenced-panel {
    :deep(.v-expansion-panel-title) {
      width: auto;
    }

    :deep(.body-2) {
      @include bb-regular-text-styles('md');
    }

    :deep(.referenced-pages) {
      display: flex;
      gap: $bb-spacing-5;
      flex-wrap: wrap;
    }

    :deep(.image-container) {
      display: flex;
      flex-direction: column;
      justify-content: space-between; /* Aligns children (start, content, end) */
      border-radius: $bb-spacing-1;
      background-color: $bb-neutral-5;
      width: 64px;
      height: 50px;
      align-items: flex-start;
      gap: $bb-spacing-1;

      img {
        max-width: 60px;
        max-height: 48px;
        align-self: center;
        cursor: pointer;
      }

      .page-index {
        @include bb-medium-text-styles('sm');
        color: $bb-neutral-1;
      }
    }
  }
}
</style>
